import { RuntimeWidget } from 'client/types';
import withErrorBoundary from 'client/widget-components/components/ErrorBoundary/withErrorBoundary';
import { useEditor } from 'client/widget-components/customHooks/useEditor';
import React, { useEffect } from 'react';
import LoadingState from './components/LoadingState';
import OrderConfirmationContent from './OrderConfirmationContent';
import { resolveDividersColor, resolvePrimaryTextColor } from './design';
import { MOCK_ORDER, RuntimeOrderConfirmationProps } from './types';
import { useEcommStrReady } from './useEcommStr';
import { useOrder } from './useOrder';

const NOT_FOUND_PATH = '/404';

const RuntimeOrderConfirmation: RuntimeWidget<
    RuntimeOrderConfirmationProps
> = ({
    layout = 'ONE_COLUMN',
    showIntro,
    textStyles,
    _styles,
    _orderState,
}) => {
    // Covers BOTH the editor and its preview mode. `useEditorContext().isInEditor`
    // only stays true in the editor itself — in preview the uiStateListener flips
    // it to false, which sent the widget down the runtime path and redirected to
    // /404. `isInPreviewOrEditor()` is true in editor and preview alike, so the
    // mock order renders in both.
    const { isInPreviewOrEditor } = useEditor();
    const isEditorOrPreview = isInPreviewOrEditor();
    const hookState = useOrder();
    const orderState = _orderState ?? hookState;
    const isStringsReady = useEcommStrReady();
    const loadingState = (
        <LoadingState
            layout={layout}
            dividerColor={resolveDividersColor(_styles)}
            primaryTextColor={resolvePrimaryTextColor(_styles)}
        />
    );

    // Redirect to /404 on empty or error states. Skipped in the editor and its
    // preview (where we render a mock order instead) and when a parent has
    // injected `_orderState` (Storybook / tests) so those flows can still
    // render the raw state.
    const shouldRedirectToNotFound =
        !isEditorOrPreview &&
        !_orderState &&
        (orderState.status === 'empty' || orderState.status === 'error');

    useEffect(() => {
        if (shouldRedirectToNotFound && typeof window !== 'undefined') {
            window.location.replace(NOT_FOUND_PATH);
        }
    }, [shouldRedirectToNotFound]);

    if (isEditorOrPreview) {
        // Hold the skeleton until phrases land so the mock order doesn't
        // briefly flash literal `nativeEcommRuntime.*` keys in the editor.
        if (!isStringsReady) {
            return loadingState;
        }
        return (
            <OrderConfirmationContent
                order={MOCK_ORDER}
                isRefreshing={false}
                layout={layout}
                showIntro={showIntro}
                textStyles={textStyles}
                styles={_styles}
            />
        );
    }

    // Keep the skeleton up until BOTH the order is fetched and the l10n
    // phrases have landed — otherwise the first paint would briefly show
    // literal `nativeEcommRuntime.*` keys before phrases arrive.
    if (orderState.status === 'success' && isStringsReady) {
        return (
            <OrderConfirmationContent
                order={orderState.order}
                isRefreshing={orderState.isRefreshing}
                layout={layout}
                showIntro={showIntro}
                textStyles={textStyles}
                styles={_styles}
            />
        );
    }

    // Loading, empty, or error: show the skeleton. For empty/error the
    // useEffect above is also redirecting to /404 — the skeleton just holds
    // the layout until navigation kicks in.
    return loadingState;
};

export default withErrorBoundary({
    Comp: RuntimeOrderConfirmation,
    logProps: true,
    componentName: 'OrderConfirmation',
    additionalInfoToLog: {
        tag: 'native-ecom',
    },
});
